/**
 * Schlüsselübergabe aus dem Kundenportal.
 *
 * Der Kunde tippt sein Passwort im Portal. Dort entsteht daraus derselbe
 * Hauptschlüssel wie hier, und daraus zwei Werte, die dieser Akten-Safe
 * braucht: der Anmeldebeweis für die API und der Schlüssel, der den privaten
 * Schlüssel auspackt. Das Portal reicht beide per `postMessage` in dieses
 * eingebettete Fenster — innerhalb desselben Browsers, ohne dass ein Server
 * je etwas davon sieht.
 *
 * ---------------------------------------------------------------------------
 * Warum nicht über die Adresszeile
 * ---------------------------------------------------------------------------
 *
 * Ein Wert im URL-Fragment (`#k=…`) geht zwar auch nicht an den Server, steht
 * aber im Verlauf, in Lesezeichen und in jedem `document.location`, das
 * irgendein Skript liest. `postMessage` an eine fest benannte Herkunft ist der
 * einzige Weg, der nichts hinterlässt.
 *
 * ---------------------------------------------------------------------------
 * Was hier zwingend geprüft wird
 * ---------------------------------------------------------------------------
 *
 *   1. `event.origin` gegen eine feste Liste. Ohne diese Prüfung könnte jede
 *      beliebige Seite, die uns einbettet, uns Schlüssel unterschieben — und
 *      damit den Akten-Safe eines fremden Kontos in unserem Fenster öffnen.
 *   2. `event.source` muss das Fenster sein, das uns eingebettet hat.
 *   3. Die Form der Nachricht, bevor irgendetwas davon benutzt wird.
 *
 * Die Gegenrichtung — wer uns überhaupt einbetten darf — steht in
 * `security-headers.conf` unter `frame-ancestors`. Beide Listen müssen
 * zusammenpassen; die hier ist die schärfere, weil der Browser die andere nur
 * durchsetzt, wenn die Kopfzeile ankommt.
 */

/** Herkünfte, von denen wir Schlüssel annehmen. Spiegelt `frame-ancestors`. */
const ERLAUBTE_HERKUENFTE: readonly string[] = [
  'https://portal.it-karsten-fischer.de',
  'https://portal.techbuddy-service.de',
  'https://app.it-karsten-fischer.de',
  'https://app.techbuddy-service.de',
];

/** Im Entwicklungsbetrieb zusätzlich der lokale Portal-Server. */
const ENTWICKLUNG: readonly string[] = ['http://localhost:5175', 'http://127.0.0.1:5175'];

const NACHRICHT_BEREIT = 'kf-akten-bereit';
const NACHRICHT_SCHLUESSEL = 'kf-akten-schluessel';
const VERSION = 1;

export interface Uebergabe {
  email: string;
  anmeldebeweis: string;
  schluessel: Uint8Array;
}

/**
 * Herkunftsprüfung und Formprüfung in einem, ohne Browser — damit sich beides
 * testen lässt. Diese Funktion entscheidet, ob ein Schlüssel angenommen wird;
 * sie ist die eigentliche Sicherheitsgrenze und hat deshalb Testabdeckung.
 *
 * `vomElternfenster` muss der Aufrufer feststellen: Ein Vergleich von
 * `event.source` mit `window.parent` lässt sich außerhalb eines Browsers nicht
 * nachbauen.
 */
export function uebergabePruefen(
  herkunft: string,
  vomElternfenster: boolean,
  daten: unknown,
  entwicklung = false,
): Uebergabe | null {
  if (!erlaubt(herkunft, entwicklung)) return null;
  // Auch eine erlaubte Herkunft darf nur reden, wenn sie uns eingebettet hat.
  // Sonst könnte ein zweites Fenster derselben Herkunft dazwischenfunken.
  if (!vomElternfenster) return null;

  if (typeof daten !== 'object' || daten === null) return null;
  const d = daten as Record<string, unknown>;
  if (d.typ !== NACHRICHT_SCHLUESSEL || d.version !== VERSION) return null;

  const { email, anmeldebeweis, schluessel } = d;
  if (typeof email !== 'string' || email === '') return null;
  if (typeof anmeldebeweis !== 'string' || anmeldebeweis === '') return null;
  // Genau 32 Byte: Das ist die Länge, die HKDF hier liefert. Ein abweichender
  // Wert bedeutet, dass etwas anderes gesendet hat als das Portal.
  if (!(schluessel instanceof Uint8Array) || schluessel.length !== 32) return null;

  return { email, anmeldebeweis, schluessel };
}

function erlaubt(herkunft: string, entwicklung: boolean): boolean {
  if (ERLAUBTE_HERKUENFTE.includes(herkunft)) return true;
  return entwicklung && ENTWICKLUNG.includes(herkunft);
}

/** Sind wir überhaupt eingebettet? */
export function istEingebettet(): boolean {
  try {
    return window.parent !== window;
  } catch {
    // Ein Zugriffsfehler bedeutet, dass ein fremdes Fenster über uns liegt —
    // eingebettet sind wir dann erst recht.
    return true;
  }
}

/**
 * Wartet auf die Schlüssel des Portals.
 *
 * Meldet dem einbettenden Fenster zuerst, dass wir bereit sind. Ohne dieses
 * Signal müsste das Portal raten, wann unser Skript läuft — eine zu früh
 * gesendete Nachricht wäre verloren, und der Kunde stünde vor einer
 * Passwortmaske, obwohl er sich gerade angemeldet hat.
 *
 * Gibt `null` zurück, wenn innerhalb der Frist nichts kommt. Dann übernimmt
 * die gewohnte Passwortmaske — dieser Weg ist eine Abkürzung, kein Ersatz.
 */
export function aufUebergabeWarten(fristMs = 8000): Promise<Uebergabe | null> {
  if (!istEingebettet()) return Promise.resolve(null);

  return new Promise((aufloesen) => {
    let erledigt = false;

    function beenden(ergebnis: Uebergabe | null): void {
      if (erledigt) return;
      erledigt = true;
      window.removeEventListener('message', empfangen);
      clearTimeout(uhr);
      aufloesen(ergebnis);
    }

    function empfangen(event: MessageEvent): void {
      const uebergabe = uebergabePruefen(
        event.origin,
        event.source === window.parent,
        event.data,
        import.meta.env.DEV,
      );
      if (uebergabe) beenden(uebergabe);
    }

    const uhr = setTimeout(() => beenden(null), fristMs);
    window.addEventListener('message', empfangen);

    // Bereitmeldung an jede erlaubte Herkunft einzeln. `'*'` wäre bequemer und
    // falsch: Es verriete jeder beliebigen einbettenden Seite, dass hier ein
    // Akten-Safe auf Schlüssel wartet.
    for (const herkunft of [...ERLAUBTE_HERKUENFTE, ...(import.meta.env.DEV ? ENTWICKLUNG : [])]) {
      try {
        window.parent.postMessage({ typ: NACHRICHT_BEREIT, version: VERSION }, herkunft);
      } catch {
        // Falsche Herkunft für dieses Fenster — der Browser verwirft still,
        // genau wie beabsichtigt.
      }
    }
  });
}
